<i18n locale="en">
{
  "error": "Unable to load the upgrade flow. Please try again.",
  "loading": "Loading the upgrade flow...",
  "missing-location": "Select a location to continue with your upgrade.",
  "title": "Upgrade to Pro"
}
</i18n>

<template>
  <div class="upgrade-to-pro-iframe-view" data-testid="upgrade-to-pro-view">
    <div
      v-if="!iframeLocationId"
      class="upgrade-to-pro-iframe-view__state"
      data-testid="upgrade-to-pro-missing-location"
    >
      {{ t('missing-location') }}
    </div>

    <template v-else>
      <div
        v-if="hestiaError"
        class="upgrade-to-pro-iframe-view__state upgrade-to-pro-iframe-view__state--error"
        data-testid="upgrade-to-pro-iframe-error"
      >
        {{ t('error') }}
      </div>

      <div
        v-if="!hestiaReady && !hestiaError"
        class="upgrade-to-pro-iframe-view__state"
        data-testid="upgrade-to-pro-iframe-loading"
      >
        <OwnProgressSpinner />
        <span>{{ t('loading') }}</span>
      </div>

      <iframe
        :key="hestiaIframeKey"
        ref="hestiaIframeRef"
        :src="hestiaIframeSrc"
        :class="[
          'upgrade-to-pro-iframe-view__iframe',
          {
            'upgrade-to-pro-iframe-view__iframe--hidden':
              !hestiaReady || Boolean(hestiaError),
          },
        ]"
        :title="t('title')"
        data-testid="upgrade-to-pro-iframe"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCoreStore } from '@/core/store/useCoreStore'
import { useHestiaIframe } from '@/features/hestia/composables/UseHestiaIframe'
import { OwnProgressSpinner } from '@/ui/OwnProgressSpinner'

const { t } = useI18n()
const { activeBrandId, activeBrandLocations, activeLocationId } =
  storeToRefs(useCoreStore())

const iframeLocationId = computed(
  () => activeLocationId.value ?? activeBrandLocations.value[0]?.id
)
const rootTargetPath = computed(() => {
  if (activeBrandLocations.value.length === 0) {
    return undefined
  }

  const locationParams = activeBrandLocations.value
    .map(({ id }) => `locations[]=${encodeURIComponent(id)}`)
    .join('&')

  return `/uber/upgrade-to-pro?${locationParams}`
})

const {
  error: hestiaError,
  iframeKey: hestiaIframeKey,
  iframeRef: hestiaIframeRef,
  iframeSrc: hestiaIframeSrc,
  isReady: hestiaReady,
} = useHestiaIframe({
  brandId: activeBrandId,
  locationId: iframeLocationId,
  rootTargetPath,
  useScopedAuthRoute: true,
})
</script>

<style lang="scss" scoped>
.upgrade-to-pro-iframe-view {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  width: 100%;

  &__iframe {
    border: 0;
    flex: 1;
    min-height: 0;
    width: 100%;

    &--hidden {
      position: absolute;
      visibility: hidden;
    }
  }

  &__state {
    align-items: center;
    color: var(--text-color-secondary);
    display: flex;
    flex: 1;
    gap: 8px;
    justify-content: center;
    padding: 24px;
    text-align: center;

    &--error {
      color: var(--status-danger);
    }
  }
}
</style>
